import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toFriendlyLimitError } from '@/lib/limitErrors';
import { toast } from 'sonner';
import { contentMetricsRepository } from '../repositories/contentMetrics.repository';
import { contentKeys } from '../services/content.keys';

interface ScrapeState {
  isLoading: boolean;
  cooldownMinutes: number | null;
  error: string | null;
}

export const useScrapePostMetrics = (
  submissionId: string,
  campaignInfluencerId: string
) => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';

  const [state, setState] = useState<ScrapeState>({
    isLoading: false,
    cooldownMinutes: null,
    error: null,
  });

  const scrape = useCallback(async () => {
    if (state.isLoading || state.cooldownMinutes) return;

    setState({ isLoading: true, cooldownMinutes: null, error: null });

    try {
      const data = await contentMetricsRepository.triggerPostScrape(submissionId);

      if (data?.error) {
        if (data.error === 'cooldown') {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            cooldownMinutes: data.remaining_minutes || 15,
          }));
          toast.info(`Please wait ${data.remaining_minutes || 15} minutes before fetching metrics again.`);
          return;
        }
        throw new Error((data as any).details || data.error);
      }

      if (data?.status === 'success') {
        setState({ isLoading: false, cooldownMinutes: null, error: null });
        queryClient.invalidateQueries({ queryKey: contentKeys.posts(orgId, campaignInfluencerId) });
        queryClient.invalidateQueries({ queryKey: contentKeys.legacyPosts(campaignInfluencerId) });
        queryClient.invalidateQueries({ queryKey: ['deliverable-aggregates'] });
        queryClient.invalidateQueries({ queryKey: contentKeys.metricsHistory(orgId, submissionId) });
        queryClient.invalidateQueries({ queryKey: contentKeys.legacyMetricsHistory(submissionId) });
        queryClient.invalidateQueries({ queryKey: ['top-performing-posts'] });
        toast.success('Metrics fetched successfully');
        return;
      }

      if (data?.status === 'failed') {
        throw new Error(data.message || "We couldn't fetch the post metrics. Please try again.");
      }

      // Unexpected response body — treat as success
      setState({ isLoading: false, cooldownMinutes: null, error: null });
      toast.success('Metrics fetched successfully');
    } catch (err: unknown) {
      const fallback =
        err instanceof Error
          ? err.message
          : "We couldn't fetch the post metrics. Please try again.";
      const message = toFriendlyLimitError(err, fallback);
      setState({ isLoading: false, cooldownMinutes: null, error: message });
      toast.error("Couldn't fetch metrics", { description: message });
    }
  }, [submissionId, campaignInfluencerId, orgId, queryClient, state.isLoading, state.cooldownMinutes]);

  return {
    scrape,
    isLoading: state.isLoading,
    cooldownMinutes: state.cooldownMinutes,
    error: state.error,
  };
};
